import { useEffect } from "react";

import { useIntl } from "react-intl";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import {
    AnchorPageRelayQuery,
    shapeAnchorContentfulResponse
} from "src/contentful";

import { anchorPageRelayQuery } from "@/__generated_contentful__/anchorPageRelayQuery.graphql";
import { CarouselProps } from "@/components/confluence/carousel/src";
import { useLogMeInD0to6 } from "@/components/confluence/hooks/use-log-me-in-d0to6.tsx";
import { PaidHeroCarousel } from "@/patterns/paid-hero/src/paid-hero-carousel";
import HeroSection from "@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero";
import {
    AnchorContentsResponse,
    PaidLandingPersonaTypes
} from "@/utils/anchor-queries/get-anchor-contents";
import { experiments, useExperimentExposure } from "@/utils/experiment";
import { FeatureExposed } from "@/utils/feature-exposed";

import { ConfluenceSoftwarePageProps } from "./confluence-software-page";
import { ConfluencePagePaidLpConflAnchor } from "./paid-lp-confl-anchor.tsx";
import { isTargetedSubAnchor } from "./subAnchors/subAnchor-util.tsx";

export interface ConfluencePagePaidLpConflDocumentationRelayProps
    extends ConfluenceSoftwarePageProps {
    anchorContents?: AnchorContentsResponse | null;
    persona?: string;
    queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageVariantPaidLpConflDocumentation = (
    pageProps: ConfluencePagePaidLpConflDocumentationRelayProps
) => {
    const intl = useIntl();
    const [exposureSubAnchorFired, fireSubAnchorExposure] =
        useExperimentExposure(experiments.cfind4782LandingPageSubAnchorV3);
    useEffect(() => {
        if (
            isTargetedSubAnchor(pageProps.subAnchor) &&
            !exposureSubAnchorFired
        ) {
            fireSubAnchorExposure();
        }
    }, [exposureSubAnchorFired, fireSubAnchorExposure]);

    const data = usePreloadedQuery(
        AnchorPageRelayQuery,
        pageProps.queryRef ||
            (null as unknown as PreloadedQuery<anchorPageRelayQuery>)
    );
    pageProps = Object.assign({}, pageProps, {
        anchorContents: shapeAnchorContentfulResponse(data)
    });

    const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6(
        {
            availableProducts: pageProps.availableProducts,
            userProfile: pageProps.userProfile
        }
    );

    const carouselSlides = [
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Documentation.dynamic-documentation.title",
                defaultMessage: "Dynamic documentation"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Documentation.dynamic-documentation.body",
                defaultMessage:
                    "Plan projects, take notes, and keep track of changes – all in one place."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/1e81unagi717g13fj44qtgpk0h8r0537.webp",
            imgAltText: "A Confluence page being edited"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Documentation.save-time-repeat-less.title",
                defaultMessage: "Save time, repeat less"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Documentation.save-time-repeat-less.body",
                defaultMessage:
                    "Scale the production of documentation for faster project delivery."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/033m853kq4f084xpcj7g6l0u2da8thu4.webp",
            imgAltText: "Start a Page with a Confluence Template"
        },
        {
            primaryText: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Documentation.centralize-knowledge.title",
                defaultMessage: "Centralize knowledge"
            }),
            description: intl.formatMessage({
                id: "ConfluenceSoftwarePageTemplate.Documentation.centralize-knowledge.body",
                defaultMessage:
                    "Stay organized and find what you need with dedicated team workspaces."
            }),
            imgSrc: "https://dam-cdn.atl.orangelogic.com/AssetLink/fpd20037c363n21cfmh45nc5m50co161.webp",
            imgAltText: "Organized workspace in Confluence"
        }
    ];

    const carouselHeroProps: CarouselProps = {
        "aria-label": "A display of various confluence features",
        "id": "confluence-documentation-carousel",
        "slides": carouselSlides
    };

    const pageId = "paid-lp-confl-documentation";

    const isLoggedIn = !!userProfileName;
    let customHeroSection;

    const getLiftShiftHeroSectionBrandUpdate = () => {
        return (
            <PaidHeroCarousel
                pageId={pageId}
                embeddedSignup={{ bundle: "confluence" }}
                carouselSlides={carouselSlides}
                shouldUseNewBrandBackground
                shouldUseTransparentBackground
            />
        );
    };

    if (isLoggedIn) {
        if (shouldShowLogMeIn) {
            customHeroSection = (
                <FeatureExposed
                    experimentKey="cfind-2801-get-paid-users-back-to-product"
                    group="Test"
                >
                    <HeroSection
                        confluenceSites={recentSites}
                        userProfileName={userProfileName}
                        hideExploreMore
                        loggedInPaid
                    />
                </FeatureExposed>
            );
        } else {
            customHeroSection = getLiftShiftHeroSectionBrandUpdate();
        }
    } else {
        // Logged out user
        customHeroSection = null;
    }

    return (
        <FeatureExposed
            experimentKey="cfind-3090-paid-confluence-product-tour"
            group="Test"
        >
            <ConfluencePagePaidLpConflAnchor
                {...pageProps}
                pageId={pageId}
                persona={"unknown" as PaidLandingPersonaTypes}
                pageVariant="brandUpdate"
                dataProductKey="confluence-PaidLpConflDocumentation"
                keyword={pageProps.keyword}
                carouselHeroProps={carouselHeroProps}
                customHeroSection={customHeroSection}
            />
        </FeatureExposed>
    );
};
